import { useState } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { Spinner } from '@core/components/ui';

/**
 * Image viewer with zoom and pan controls.
 */
export function ImageViewer({ url }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  return (
    <div className="flex flex-col h-full">
      <TransformWrapper
        initialScale={1}
        minScale={0.25}
        maxScale={5}
        centerOnInit
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="flex items-center justify-center gap-2 py-2 px-4 border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)] shrink-0">
              <button
                onClick={() => zoomOut()}
                className="p-1 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer"
                aria-label={t('dataroom.viewer.zoomOut')}
              >
                <Icon icon="mdi:minus" className="size-5" />
              </button>
              <button
                onClick={() => resetTransform()}
                className="p-1 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer"
                aria-label={t('dataroom.viewer.resetZoom')}
              >
                <Icon icon="mdi:fit-to-page-outline" className="size-5" />
              </button>
              <button
                onClick={() => zoomIn()}
                className="p-1 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer"
                aria-label={t('dataroom.viewer.zoomIn')}
              >
                <Icon icon="mdi:plus" className="size-5" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden bg-[var(--color-bg-muted)] relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <Spinner size="lg" />
                </div>
              )}
              {hasError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-[var(--color-danger)]">
                  <Icon icon="mdi:image-broken" className="size-12 mb-2" />
                  <p className="text-sm">{t('dataroom.viewer.imageLoadFailed')}</p>
                </div>
              )}
              <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }} contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src={url}
                  alt={t('dataroom.viewer.imageAlt')}
                  className="max-w-full max-h-full object-contain"
                  onLoad={() => setIsLoading(false)}
                  onError={() => {
                    setIsLoading(false);
                    setHasError(true);
                  }}
                />
              </TransformComponent>
            </div>
          </>
        )}
      </TransformWrapper>
    </div>
  );
}

ImageViewer.propTypes = {
  url: PropTypes.string.isRequired,
};
